import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';

import { Breadcrumbs, Button, Search, Select, TableList, useDebounce } from '@utilities';

import ActionsToolbar from './components/ActionsToolbar';
import CreateFolder from './components/CreateFolder';
import CreateItem from './components/CreateItem';
import { getPageKey, searchPlaceholderText } from './utilities/helpers';

import styles from './_index.module.scss';

const ListPage = ({
  createFolder,
  createItem,
  filterOptions,
  folderActions,
  folderColumns,
  folderSize,
  isCreateModalActive,
  itemActions,
  itemColumns,
  itemSize,
  onCreate,
  onExpandItemRow,
  readFolders,
  readItems,
  renderRowExpanded,
  setIsCreateModalActive,
  type,
  workspace,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSortBy = [{ desc: true, id: 'created_datetime' }];
  const pageNumber = parseInt(searchParams.get('itemsPage'), 10);
  const rowNumber = parseInt(searchParams.get('itemsRow'), 10);
  const [expandedFolder, setExpandedFolder] = useState(null);
  const [filters, setFilters] = useState(() => {
    const mapValuesToOptions = (values) => {
      if (!values?.length) return [];
      const allOptions = filterOptions.flatMap((group) => group.options);
      return values
        .map((value) => allOptions.find((option) => option.value === value))
        .filter(Boolean);
    };

    const getUrlFilters = () => {
      const urlFilterString = searchParams.get('filters');
      return urlFilterString?.split(',').filter(Boolean) || null;
    };

    const getStoredFilters = () => {
      const storedFilters = JSON.parse(localStorage.getItem('list_page_filters')) || {};
      const pageKey = getPageKey({ type, workspace });
      return storedFilters[pageKey] || [];
    };

    const urlFilters = getUrlFilters();
    const rawFilters = urlFilters || getStoredFilters();

    return mapValuesToOptions(rawFilters);
  });

  const [folders, setFolders] = useState([]);
  const [foldersPage, setFoldersPage] = useState(1);
  const [foldersPageCount, setFoldersPageCount] = useState(0);
  const [foldersSortBy, setFoldersSortBy] = useState(initialSortBy);
  const [initialExpandedRow, setInitialExpandedRow] = useState(
    Number.isNaN(rowNumber) ? null : rowNumber
  );
  const [isFoldersLoading, setIsFoldersLoading] = useState(false);
  const [isItemsLoading, setIsItemsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [itemsPage, setItemsPage] = useState(pageNumber || 1);
  const [itemsPageCount, setItemsPageCount] = useState(0);
  const [itemsSortBy, setItemsSortBy] = useState(initialSortBy);
  const [searchQuery, setSearchQuery, { isDebouncing, signal }] = useDebounce(
    searchParams.get('search') || ''
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [shouldExpandFirstRow, setShouldExpandFirstRow] = useState(false);
  const folderControllerRef = useRef(null);
  const isSelectedFolders = selectedRows.some((selectedRow) => selectedRow?.isFolder);
  const isSelectedItems = selectedRows.some((selectedRow) => selectedRow?.isItem);
  const itemControllerRef = useRef(null);

  const getFolders = async () => {
    if (!readFolders) {
      return;
    }
    setIsFoldersLoading(true);
    folderControllerRef?.current?.abort();
    const controller = new AbortController();
    folderControllerRef.current = controller;

    try {
      const response = await readFolders({
        order: foldersSortBy[0]?.desc ? 'desc' : 'asc',
        page: foldersPage,
        query: searchQuery,
        signal: folderControllerRef?.current?.signal,
        size: folderSize,
        sortBy: foldersSortBy[0]?.id,
        tags: filters
          ?.filter((option) => option?.value.includes('_folder') || option?.value.includes('_all'))
          .map((option) => option?.value.replace('_folder', '').replace('_all', '')),
      });

      setFoldersPageCount(Math.ceil(response?.data?.total / folderSize));
      setFolders(response?.data?.folders);
      setIsFoldersLoading(false);
      folderControllerRef.current = null;
    } catch (error) {
      if (error?.response?.status > 200) {
        console.error(error);
        setIsFoldersLoading(false);
      }
    }
  };

  const getItems = async ({ expandItemsFirstRow = false }) => {
    setIsItemsLoading(true);
    setShouldExpandFirstRow(false);
    if (Number.isNaN(rowNumber)) {
      setInitialExpandedRow(null);
    }

    itemControllerRef?.current?.abort();
    const controller = new AbortController();
    itemControllerRef.current = controller;

    try {
      const response = await readItems({
        folderId: expandedFolder?.id,
        order: itemsSortBy[0]?.desc ? 'desc' : 'asc',
        page: itemsPage,
        query: searchQuery,
        signal: itemControllerRef?.current?.signal,
        size: itemSize,
        sortBy: itemsSortBy[0]?.id,
        tags: filters
          ?.filter((option) => option?.value.includes('_item') || option?.value.includes('_all'))
          .map((option) => option?.value.replace('_item', '').replace('_all', '')),
      });

      let itemsList = response?.data.items;

      if (expandItemsFirstRow) {
        setInitialExpandedRow(0);
      }

      setItemsPageCount(Math.ceil(response?.data?.total / itemSize));
      setItems(itemsList);

      setIsItemsLoading(false);
      itemControllerRef.current = null;
    } catch (error) {
      if (error?.response?.status > 200) {
        console.error(error);
        setIsItemsLoading(false);
      }
    }
  };

  const handleSubmit = ({
    refreshFolders = false,
    refreshItems = false,
    resetFoldersPage = false,
    resetItemsPage = false,
    resetItemsPageExpandFirstRow = false,
  }) => {
    setSelectedRows([]);

    if (refreshFolders || (resetFoldersPage && foldersPage === 1)) {
      getFolders();
    }

    if (refreshItems || (resetItemsPage && itemsPage === 1)) {
      getItems({});
    }

    if (resetFoldersPage) {
      setFoldersPage(1);
    }

    if (resetItemsPage) {
      setItemsPage(1);
    }

    if (resetItemsPageExpandFirstRow) {
      if (itemsPage === 1) {
        getItems({ expandItemsFirstRow: true });
      } else {
        setShouldExpandFirstRow(true);
        setItemsPage(1);
      }
    }
  };

  const pageKey = getPageKey({ type, workspace });

  const handleFilterChange = (selectedOptions) => {
    setFilters(selectedOptions);
    const storedFilters = JSON.parse(localStorage.getItem('list_page_filters')) || {};

    const filterValues = selectedOptions.map((filter) => filter.value);
    storedFilters[pageKey] = filterValues;
    localStorage.setItem('list_page_filters', JSON.stringify(storedFilters));
  };

  useEffect(() => {
    setSearchParams({
      ...(filters.length > 0 ? { filters: filters.map((filter) => filter.value).join(',') } : {}),
      ...(searchQuery ? { search: searchQuery } : {}),
    });
  }, [filters, signal]);

  useEffect(() => {
    setFoldersPage(1);
  }, [foldersSortBy, signal]);

  useEffect(() => {
    if (!pageNumber) setItemsPage(1);
  }, [expandedFolder, itemsSortBy, signal]);

  useEffect(() => {
    getFolders();
  }, [filters, foldersPage, foldersSortBy, signal]);

  useEffect(() => {
    if (expandedFolder) {
      setSearchQuery('');
    }
  }, [expandedFolder]);

  useEffect(() => {
    getItems({ expandItemsFirstRow: shouldExpandFirstRow });
  }, [expandedFolder, filters, itemsPage, itemsSortBy, signal]);

  return (
    <>
      <div className={styles['list-page-header']}>
        <div className={styles['list-page-search']}>
          <Search
            isSearching={
              (isDebouncing || isItemsLoading || isFoldersLoading) && searchQuery.trim().length > 0
            }
            onChange={(query) => {
              if (query.trim().length > 0 || query === '') {
                setSearchQuery(query);
              }
            }}
            placeholder={searchPlaceholderText(workspace)}
            value={searchQuery}
          />
          <Button data-testid="create-button" onClick={onCreate} text={`Create ${type}`} />
        </div>
        <div className={styles['list-page-filters']}>
          <div>
            <Select
              isFullHeightMenuList
              isMulti
              label="Filters"
              onChange={handleFilterChange}
              options={filterOptions}
              placeholder="Filter by"
              value={filters}
            />
          </div>
          {createFolder && (
            <CreateFolder
              createFolder={createFolder}
              onSubmit={() => handleSubmit({ resetFoldersPage: true })}
            />
          )}
        </div>
      </div>
      {!expandedFolder && folderColumns && (
        <TableList
          columns={folderColumns}
          currentPage={foldersPage}
          data={folders}
          expandedRow={expandedFolder}
          initialSortBy={initialSortBy}
          isDisabled={isSelectedItems}
          isLoading={isDebouncing || isFoldersLoading}
          onExpandRow={(row) =>
            setExpandedFolder((prevState) => (prevState?.id === row?.id ? null : row))
          }
          pageCount={foldersPageCount}
          renderEmptyMessage={() =>
            searchQuery !== '' || filters?.length > 0 ? (
              <>
                <h3>No results found.</h3>
                <p>Try refining your search or filters.</p>
              </>
            ) : (
              <>
                <h3>Folders will appear here.</h3>
                <p>Use them to stay organized or share them with colleagues.</p>
              </>
            )
          }
          renderRowHovered={({ row, setVisibleActionsCount }) => (
            <ActionsToolbar
              actions={folderActions}
              isHoverAction={true}
              onSubmit={handleSubmit}
              selectedRows={[row?.original]}
              setVisibleActionsCount={setVisibleActionsCount}
              type={`${type} Folder`}
            />
          )}
          selectedRows={selectedRows}
          setPage={setFoldersPage}
          setSelectedRows={setSelectedRows}
          setSortBy={setFoldersSortBy}
        />
      )}
      {expandedFolder && (
        <Breadcrumbs
          options={[
            {
              label: `All ${type}s`,
              onClick: () => {
                setExpandedFolder(null);
                setSearchQuery('');
              },
            },
            { label: expandedFolder?.name, value: expandedFolder?.id },
          ]}
        />
      )}
      <TableList
        columns={itemColumns}
        currentPage={itemsPage}
        data={items}
        initialExpandedRow={initialExpandedRow}
        initialSortBy={initialSortBy}
        isDisabled={isSelectedFolders}
        isLoading={isDebouncing || isItemsLoading}
        onExpandRow={onExpandItemRow}
        pageCount={itemsPageCount}
        renderEmptyMessage={() =>
          searchQuery !== '' || filters?.length > 0 ? (
            <>
              <h3>No results found.</h3>
              <p>Try refining your search or filters.</p>
            </>
          ) : (
            <>
              <h3>{type}s will appear here.</h3>
              <p> Get started by creating one from the top right.</p>
            </>
          )
        }
        renderRowExpanded={({ row }) => renderRowExpanded({ handleSubmit, itemsPage, row })}
        renderRowHovered={({ row, setVisibleActionsCount }) => (
          <ActionsToolbar
            actions={itemActions}
            expandedFolder={expandedFolder}
            isHoverAction={true}
            itemsPage={itemsPage}
            itemsRow={row?.id}
            onSubmit={handleSubmit}
            row={row}
            selectedRows={[row?.original]}
            setVisibleActionsCount={setVisibleActionsCount}
            type={type}
          />
        )}
        selectedRows={selectedRows}
        setPage={setItemsPage}
        setSelectedRows={setSelectedRows}
        setSortBy={setItemsSortBy}
        size={itemSize}
      />

      {selectedRows.length >= 1 && (
        <ActionsToolbar
          actions={isSelectedFolders ? folderActions : itemActions}
          expandedFolder={expandedFolder}
          itemsPage={itemsPage}
          onClear={() => setSelectedRows([])}
          onSubmit={handleSubmit}
          selectedRows={selectedRows}
          type={isSelectedFolders ? `${type} Folder` : type}
        />
      )}
      {createItem && (
        <CreateItem
          createItem={createItem}
          isCreateModalActive={isCreateModalActive}
          onSubmit={() => {
            handleSubmit({
              resetItemsPageExpandFirstRow: true,
            });
          }}
          setIsCreateModalActive={setIsCreateModalActive}
          type={type}
        />
      )}
    </>
  );
};

ListPage.defaultProps = {
  folderSize: 5,
  itemSize: 10,
  renderRowExpanded: () => {},
};

ListPage.propTypes = {
  createFolder: PropTypes.func,
  createItem: PropTypes.func,
  filterOptions: PropTypes.array,
  folderActions: PropTypes.array,
  folderColumns: PropTypes.array,
  folderSize: PropTypes.number,
  isCreateModalActive: PropTypes.bool,
  itemActions: PropTypes.array,
  itemColumns: PropTypes.array.isRequired,
  itemSize: PropTypes.number,
  onCreate: PropTypes.func,
  onExpandItemRow: PropTypes.func,
  readFolders: PropTypes.func,
  readItems: PropTypes.func.isRequired,
  renderRowExpanded: PropTypes.func,
  setIsCreateModalActive: PropTypes.func,
  type: PropTypes.string.isRequired,
  workspace: PropTypes.string,
};

export default ListPage;
